import React, { useState, useMemo, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Expander from '@amzn/meridian/expander';
import Select, { SelectOption } from '@amzn/meridian/select';
import { TEXTS } from '../../utils/constants';
import { formatDuration } from './laborOpportunitiesModel';
import { matchProcessActuals } from './flowPlannerAdherenceModel';
import {
  GROUP_VOLUME_ACTUAL,
  NEW_VIEW_GROUP_VOLUME_ACTUAL,
  SELF_MATCHED_VOLUME_ACTUAL_ROWS,
  GROUP_VOLUME_SOURCE
} from './FlowPlannerGridConfig';
import {
  getActiveCells,
  applyRemainder,
  MatrixPercentInput,
  MeterStrip,
  WORKING_COLOR,
  BREAK_PAID,
  BREAK_UNPAID
} from './VolumeDistribution';
import { ActualFragment, actualAt } from './shared/actuals';
import './shared/flowPlannerShared.css';

const METRICS = [
  { value: 'all', label: 'All Metrics' },
  { value: 'volume', label: 'Volume' },
  { value: 'rate', label: 'Rate' },
  { value: 'headcount', label: 'Headcount' }
];

// New-view groups that show volume only on parent rows; child paths drop it.
const NEW_VIEW = 'new';
const CLASSIC_VIEW = 'classic';
const PARENT_ONLY_VOLUME_GROUPS = new Set(['INDUCT_GROUP', 'PROBLEM_SOLVE', 'NON_CON_STOW', 'OUTBOUND']);

// New view, All Metrics: these parents repeat a volume that is already shown by an
// ancestor or a child, so their row is left blank rather than dashed.
const BLANK_VOLUME_ROWS = new Set(['INDUCT_FLOW', 'PICK_TO_BUFFER', 'STOW', 'NON_CON_STOW']);

const DEPTH_INDENT_PX = { 0: 0, 1: 24, 2: 48, 3: 72 };
const formatNumber = (value) => (value === null ? 'N/A' : (value || 0).toLocaleString());

const METRIC_FORMATTERS = {
  volume: (s) => formatNumber(s.volume),
  rate: (s) => formatNumber(s.rate),
  workTime: (s) => `${s.workTime || 0}m`,
  headcount: (s) => (s.headcount || 0).toFixed(2)
};

// ── Actuals (additive-only) ───────────────────────────────────────────────────
// Purely additive: a blue "actual /" fragment before the existing planned number.
// Never changes how a planned value is computed; emits nothing when actuals are
// hidden or the row has no matching adherence series.

// A row's matched actuals ({ volume, rate, headcount } arrays), or null.
function getRowActual(ctx, node) {
  if (!ctx || !ctx.showActuals || !ctx.byProcess) return null;
  // The view's group map, then the row's own series: Induct controllers carry
  // actualsVolumeProcess, and a few leaves are allowed to match their own name. Any
  // other row name is not matched, so group totals are not repeated down the tree.
  const selfMatched = SELF_MATCHED_VOLUME_ACTUAL_ROWS.has(node.key) ? node.key : null;
  const selfMatchedName = SELF_MATCHED_VOLUME_ACTUAL_ROWS.has(node.displayName) ? node.displayName : null;
  const candidates = [ctx.volumeActual?.[node.key], node.actualsVolumeProcess, selfMatched, selfMatchedName].filter(
    Boolean
  );
  return matchProcessActuals(ctx.byProcess, candidates);
}

const ToggleButton = ({ open, onClick, label }) => (
  <button type="button" className="fp-plan-toggle" onClick={onClick} aria-expanded={open} aria-label={label}>
    {open ? '▼' : '▶'}
  </button>
);

const ToggleSpacer = () => <span className="fp-plan-toggle-spacer" aria-hidden="true" />;

const PVT_BREACH_THRESHOLD = 0.95;

const PvtPill = ({ pvt }) => {
  if (!pvt) return null;
  const planned = pvt.planned || 0;
  const target = pvt.target || 0;
  if (!planned && !target) return null;
  // Only flag a breach when achievement falls below 95% — small overshoots
  // (e.g. 285/286) shouldn't read as alarm-level.
  const breach = target > 0 && planned / target < PVT_BREACH_THRESHOLD;
  return (
    <span className="fp-plan-pvt" data-breach={breach} title={`Planned ${planned} / Target ${target}`}>
      <span>{planned.toLocaleString()}</span>
      <span className="fp-plan-pvt-sep">/</span>
      <span>{target.toLocaleString()}</span>
    </span>
  );
};

const ThreeCellGrid = ({ slot, actual, ended, hideVolume = false }) => {
  // hideVolume drops the Vol line only; volume still counts towards emptiness so a
  // zero-by-config row reads "Rate 0 · HC 0.0" rather than "—" (no plan data).
  const allZero = !slot.volume && !slot.rate && !slot.headcount;
  // Volume is the only actual surfaced here: headcount is station-wide and rate is
  // hidden for now, so neither can keep an otherwise-empty cell alive.
  const hasActual = actual && !hideVolume && actual.volume !== undefined;
  if (allZero && !hasActual) return <div className="fp-plan-mini-empty">—</div>;
  const struck = (v) => (ended && v !== undefined ? 'fp-plan-struck' : undefined);
  return (
    <div className="fp-plan-mini-stack">
      {!hideVolume && (
        <span className="fp-plan-mini-primary">
          <span className="fp-plan-mini-label">Vol</span>
          {actual && <ActualFragment value={actual.volume} format={formatNumber} />}
          <span className={struck(actual?.volume)}>{formatNumber(slot.volume)}</span>
        </span>
      )}
      {/* Rate and HC actuals are not surfaced, so their planned values never strike -
          there is nothing to compare a finished window against. */}
      <span className="fp-plan-mini-secondary">
        <span className="fp-plan-mini-label">Rate</span>
        <span>{formatNumber(slot.rate)}</span>
        <span className="sep" aria-hidden="true">
          ·
        </span>
        <span className="fp-plan-mini-label">HC</span>
        <span className="fp-plan-mini-hc">{(slot.headcount || 0).toFixed(1)}</span>
      </span>
    </div>
  );
};

const SingleMetricCell = ({ slot, metric, actual, ended }) => {
  const formatted = METRIC_FORMATTERS[metric](slot);
  const empty = formatted === '0' || formatted === '0.00' || formatted === '0m';
  // Volume only: headcount actuals are station-wide and rate actuals are hidden. With
  // no actual to compare against, rate and HC never strike once a window has ended.
  const actualValue = metric === 'volume' && actual ? actual.volume : undefined;
  const struck = ended && actualValue !== undefined;
  return (
    <span className={`fp-plan-value${empty && actualValue === undefined ? ' fp-plan-value-muted' : ''}`}>
      <ActualFragment value={actualValue} format={formatNumber} />
      <span className={struck ? 'fp-plan-struck' : undefined}>{empty ? '—' : formatted}</span>
    </span>
  );
};

// Per-interval distribution for a volume-source group row: the timing strip above the
// volume, the editable percentage (or its auto remainder) below. Cells exist only for
// periods the process actually runs in.
const distributionParts = (dist, slotIndex) => {
  const cell = dist.cellBySlot[slotIndex];
  if (!cell) return null;
  return {
    strip: (
      <div className="fp-plan-strip-top">
        <MeterStrip
          interval={cell.interval}
          processEvent={dist.processEvent}
          color={WORKING_COLOR}
          breakPaid={BREAK_PAID}
          breakUnpaid={BREAK_UNPAID}
        />
      </div>
    ),
    pct: <MatrixPercentInput pct={cell.pct} isAuto={cell.isAuto} onChange={(v) => dist.onChange(slotIndex, v)} />
  };
};

// Group rows all use the detail rows' weight, so a controller total does not read
// lighter than the processes beneath it.
const GroupRow = ({
  row,
  depth,
  isOpen,
  onToggle,
  actualsCtx,
  ended,
  isNewView = false,
  dist = null,
  blank = false
}) => {
  const isTopLevel = depth === 0;
  const rowActual = getRowActual(actualsCtx, row);
  if (blank) {
    return (
      <tr>
        <td className="fp-plan-cell fp-plan-row-label">
          <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
            <ToggleButton
              open={isOpen}
              onClick={onToggle}
              label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
            />
            <span className="fp-plan-row-text">{row.displayName}</span>
          </span>
        </td>
        <td className="fp-plan-cell fp-plan-cell--center" />
        {row.slots.map((_, i) => (
          <td key={i} className="fp-plan-cell fp-plan-cell--center" />
        ))}
      </tr>
    );
  }
  return (
    <tr>
      <td className="fp-plan-cell fp-plan-row-label">
        <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
          <ToggleButton
            open={isOpen}
            onClick={onToggle}
            label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
          />
          <span className="fp-plan-row-text">{row.displayName}</span>
        </span>
      </td>
      <td className="fp-plan-cell fp-plan-cell--center">{!dist && <span className="fp-plan-value-muted">—</span>}</td>
      {row.slots.map((slot, i) => {
        const empty = !slot.volume && !slot.headcount;
        const actualVol = rowActual ? actualAt(rowActual.volume, i) : undefined;
        // Null for a period the process does not run in: the volume still shows, but
        // there is no distribution cell to edit, so no percentage and no strip.
        const parts = dist ? distributionParts(dist, i) : null;
        // Nothing to say for this period, so the cell is left blank rather than
        // reading "—" above "(0.0 HC)".
        if (dist && empty && actualVol === undefined && !parts) {
          return <td key={i} className="fp-plan-cell fp-plan-cell--center" />;
        }
        return (
          <td key={i} className="fp-plan-cell fp-plan-cell--center">
            {parts?.strip}
            {empty && actualVol === undefined ? (
              <span className="fp-plan-value-muted">—</span>
            ) : (
              <span className={`fp-plan-value${isNewView || isTopLevel ? ' is-emphasized' : ''}`}>
                {isNewView && <span className="fp-plan-mini-label">Vol</span>}
                <ActualFragment value={actualVol} format={formatNumber} />
                <span className={ended?.[i] && actualVol !== undefined ? 'fp-plan-struck' : undefined}>
                  {(slot.volume || 0).toLocaleString()}
                </span>
                {!isOpen && isTopLevel && !dist && (
                  <span className="fp-plan-aggregate-secondary">{`(${(slot.headcount || 0).toFixed(1)} HC)`}</span>
                )}
              </span>
            )}
            {dist && parts && (
              <div className="fp-plan-pct-line">
                <span className="fp-plan-mini-label">Vol%</span>
                {parts.pct}
              </div>
            )}
            {dist && (
              <div className="fp-plan-hc-line">
                <span className="fp-plan-aggregate-secondary">{`(${(slot.headcount || 0).toFixed(1)} HC)`}</span>
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
};

const DetailRow = ({ row, depth, metric, actualsCtx, ended, hideVolume = false, isOpen, onToggle }) => {
  const rowActual = getRowActual(actualsCtx, row);
  const actualForSlot = (i) =>
    rowActual
      ? {
          volume: actualAt(rowActual.volume, i),
          rate: actualAt(rowActual.rate, i),
          headcount: actualAt(rowActual.headcount, i)
        }
      : null;
  return (
    <tr>
      <td className="fp-plan-cell fp-plan-row-label">
        <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
          {onToggle ? (
            <ToggleButton
              open={!!isOpen}
              onClick={onToggle}
              label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
            />
          ) : (
            <ToggleSpacer />
          )}
          <span className="fp-plan-row-text">
            {row.displayName}
            {row.splitPercentage != null && (
              <span className="fp-plan-split-pct">{` (${row.splitPercentage.toFixed(0)}%)`}</span>
            )}
          </span>
        </span>
      </td>
      <td className="fp-plan-cell fp-plan-cell--center">
        {row.pvt ? <PvtPill pvt={row.pvt} /> : <span className="fp-plan-value-muted">—</span>}
      </td>
      {row.slots.map((slot, i) => (
        <td key={i} className="fp-plan-cell fp-plan-cell--center">
          {metric === 'all' ? (
            <ThreeCellGrid slot={slot} actual={actualForSlot(i)} ended={ended?.[i]} hideVolume={hideVolume} />
          ) : (
            <SingleMetricCell slot={slot} metric={metric} actual={actualForSlot(i)} ended={ended?.[i]} />
          )}
        </td>
      ))}
    </tr>
  );
};

const WipRow = ({ stowWIP, columnCount }) => (
  <tr className="fp-plan-wip-row">
    <td className="fp-plan-cell fp-plan-row-label">
      <span className="fp-plan-row-label-inner">
        <ToggleSpacer />
        <span className="fp-plan-wip-chip">Stow WIP</span>
      </span>
    </td>
    <td className="fp-plan-cell fp-plan-cell--center">
      <span className="fp-plan-value-muted">—</span>
    </td>
    {stowWIP.slice(0, columnCount).map((value, i) => (
      <td
        key={i}
        className="fp-plan-cell fp-plan-cell--center"
        title="Work In Progress: Cumulative Induct - Cumulative Stow">
        {value === 0 ? (
          <span className="fp-plan-value-muted">—</span>
        ) : (
          <span className="fp-plan-value is-emphasized">{value.toLocaleString()}</span>
        )}
      </td>
    ))}
  </tr>
);

function renderNode(
  node,
  depth,
  expanded,
  toggle,
  metric,
  actualsCtx,
  ended,
  hideVolume = false,
  isNewView = false,
  distByKey = null,
  blankVolumeKeys = null
) {
  const isGroup = !!node.children;
  const key = node.key || node.displayName;
  const isOpen = expanded.includes(key);
  // Leaf DetailRows only; GroupRows always keep volume. Regrouped rows carry the flag.
  const hideVol = hideVolume || !!node.hideVolume;

  // A process row with children (a Stow variant) keeps its own PVT/rate/HC cells and
  // just gains a toggle. Synthetic aggregates have pvt === null → GroupRow.
  if (!isGroup || node.pvt) {
    const elements = [
      <DetailRow
        key={key}
        row={node}
        depth={depth}
        metric={metric}
        actualsCtx={actualsCtx}
        ended={ended}
        hideVolume={hideVol}
        isOpen={isGroup ? isOpen : undefined}
        onToggle={isGroup ? () => toggle(key) : undefined}
      />
    ];
    if (isGroup && isOpen) {
      node.children.forEach((child) => {
        elements.push(
          ...renderNode(
            child,
            depth + 1,
            expanded,
            toggle,
            metric,
            actualsCtx,
            ended,
            hideVol,
            isNewView,
            distByKey,
            blankVolumeKeys
          )
        );
      });
    }
    return elements;
  }

  const elements = [
    <GroupRow
      key={key}
      row={node}
      depth={depth}
      isOpen={isOpen}
      onToggle={() => toggle(key)}
      actualsCtx={actualsCtx}
      ended={ended}
      isNewView={isNewView}
      dist={distByKey?.[node.key] || null}
      blank={!!blankVolumeKeys?.has(node.key)}
    />
  ];

  if (isOpen) {
    node.children.forEach((child) => {
      elements.push(
        ...renderNode(
          child,
          depth + 1,
          expanded,
          toggle,
          metric,
          actualsCtx,
          ended,
          hideVol,
          isNewView,
          distByKey,
          blankVolumeKeys
        )
      );
    });
  }
  return elements;
}

function collectAllGroupKeys(nodes) {
  const keys = [];
  for (const node of nodes) {
    if (node.children && !node.hidden) {
      keys.push(node.key || node.displayName);
      keys.push(...collectAllGroupKeys(node.children));
    }
  }
  return keys;
}

const LaborPlanGrid = ({
  gridCycleData,
  intervals = [],
  actuals = null,
  showActuals = false,
  endedByInterval = [],
  gridLayout = NEW_VIEW,
  distribution = null,
  selectedCycle = null
}) => {
  const [expanded, setExpanded] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);
  const gridView = gridLayout === NEW_VIEW ? NEW_VIEW : CLASSIC_VIEW;
  const isNewView = gridView === NEW_VIEW;

  const metricOptions = METRICS;

  const toggle = (key) => setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const { rows = [], stowWIP = [] } = gridCycleData || {};

  const displayRows = useMemo(() => {
    if (gridView !== NEW_VIEW) return rows;

    const find = (key) => rows.find((r) => r.key === key);
    const leavesOf = (row) => (row?.children?.length ? row.children : row ? [row] : []);
    const hideVolumeDeep = (node) =>
      node.children?.length ? { ...node, children: node.children.map(hideVolumeDeep) } : { ...node, hideVolume: true };
    // Volume stays with the volume-source group, but headcount has to come from the
    // regrouped children or the row undercounts the processes shown beneath it.
    const withChildHeadcounts = (sourceSlots, children) =>
      (sourceSlots || []).map((slot, i) => ({
        ...slot,
        headcount: children.reduce((sum, child) => sum + (child.slots?.[i]?.headcount || 0), 0)
      }));

    const induct = find('INDUCT_GROUP');
    const waterSpider = find('WATER_SPIDER');
    const divert = find('DIVERT');
    const pickToBuffer = find('PICK_TO_BUFFER');
    const stow = find('STOW');
    const problemSolve = find('PROBLEM_SOLVE');
    const nonConStow = find('NON_CON_STOW');
    if (!induct?.children || !stow?.children) return rows;

    const absorbed = new Set([
      'INDUCT_GROUP',
      'WATER_SPIDER',
      'DIVERT',
      'PICK_TO_BUFFER',
      'STOW',
      'PROBLEM_SOLVE',
      'NON_CON_STOW'
    ]);

    const inboundChildren = [
      ...leavesOf(waterSpider),
      {
        key: 'INDUCT_FLOW',
        displayName: 'Induct',
        totalVolume: induct.totalVolume,
        pvt: null,
        slots: induct.slots,
        children: induct.children
      },
      ...leavesOf(divert).map((node) => ({ ...node, displayName: 'Diverter' }))
    ];
    const inbound = hideVolumeDeep({
      ...induct,
      displayName: 'Inbound',
      slots: withChildHeadcounts(induct.slots, inboundChildren),
      children: inboundChildren
    });

    const sortChildren = [];
    if (pickToBuffer) sortChildren.push(pickToBuffer);
    sortChildren.push(stow);
    sortChildren.push(...leavesOf(problemSolve).map(hideVolumeDeep));
    if (nonConStow) sortChildren.push(hideVolumeDeep(nonConStow));

    const sort = {
      key: 'SORT',
      displayName: 'Sort',
      totalVolume: stow.totalVolume,
      pvt: null,
      slots: withChildHeadcounts(stow.slots, sortChildren),
      children: sortChildren
    };

    const rest = rows
      .filter((r) => !absorbed.has(r.key))
      .map((r) => (PARENT_ONLY_VOLUME_GROUPS.has(r.key) ? hideVolumeDeep(r) : r));

    return [inbound, sort, ...rest];
  }, [rows, gridView]);

  const allGroupKeys = useMemo(() => collectAllGroupKeys(displayRows), [displayRows]);

  // With actuals off or absent, byProcess is undefined and getRowActual()
  // short-circuits, so planned output stays byte-for-byte identical.
  // Volume Distribution folded into the grid: one bundle per volume-source group row,
  // reusing getActiveCells so the active periods, derived volumes and auto remainder
  // behave exactly as the standalone panel does.
  const showDistribution = gridView === NEW_VIEW && !!distribution && !!selectedCycle;
  const distByKey = useMemo(() => {
    if (!showDistribution) return null;
    const out = {};
    Object.entries(GROUP_VOLUME_SOURCE).forEach(([rowKey, process]) => {
      const { processEvent, cells, activeSlotIndices } = getActiveCells(
        selectedCycle,
        process,
        intervals,
        distribution.distributions || {},
        distribution.totalVolume,
        distribution.processEvents,
        false
      );
      if (!processEvent) return;
      const cellBySlot = {};
      cells.forEach((c) => {
        cellBySlot[c.slotIndex] = c;
      });
      out[rowKey] = {
        processEvent,
        cellBySlot,
        onChange: (slotIndex, value) => {
          const current = distribution.distributions?.[selectedCycle]?.[process]?.percentages;
          distribution.onChange(selectedCycle, process, applyRemainder(current, slotIndex, value, activeSlotIndices));
        }
      };
    });
    return out;
  }, [showDistribution, selectedCycle, intervals, distribution]);

  const blankVolumeKeys = gridView === NEW_VIEW && selectedMetric === 'all' ? BLANK_VOLUME_ROWS : null;

  const actualsCtx = {
    showActuals: showActuals && !!actuals?.byProcess,
    byProcess: actuals?.byProcess,
    volumeActual: gridView === NEW_VIEW ? NEW_VIEW_GROUP_VOLUME_ACTUAL : GROUP_VOLUME_ACTUAL
  };

  // Fully expanded by default. Re-runs when the cycle or the view changes, since both
  // rebuild the hierarchy; a manual collapse is left alone until then.
  useEffect(() => setExpanded(isNewView ? allGroupKeys : []), [gridCycleData, allGroupKeys, isNewView]);
  if (!gridCycleData) return null;

  const wipBeforeKey = gridView === NEW_VIEW ? 'SORT' : 'STOW';
  const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every((k) => expanded.includes(k));
  const expandAll = () => setExpanded(allGroupKeys);
  const collapseAll = () => setExpanded([]);

  const slotCount = intervals.length || rows[0]?.slots.length || 0;

  const toolbar = (
    <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300" width="100%">
      {showDistribution && distribution.isOverridden && (
        <button type="button" className="vd-revert" onClick={distribution.onRevert}>
          Revert volume distribution
        </button>
      )}
      <Text type={TEXTS.T3}>View:</Text>
      <Select value={selectedMetric} onChange={setSelectedMetric} width={140} size="small">
        {metricOptions.map((m) => (
          <SelectOption key={m.value} value={m.value} label={m.label} />
        ))}
      </Select>
    </Row>
  );

  return (
    <div className="fp-theme">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        {/* New view lifts the toolbar onto the Expander's title line (as a child it either
            took a row of its own or got clipped by the collapse container, and inside
            `title` a control would toggle the panel). Classic keeps it as the first row
            of the body, where it has always been. */}
        <div className="fp-plan-expander">
          {isNewView && isExpanded && <div className="fp-plan-toolbar">{toolbar}</div>}
          <Expander open={isExpanded} onChange={setIsExpanded} title="Labor Plan" type="inline" spacingInset="300">
            <Column spacing="100" alignmentHorizontal="stretch" width="100%">
              {!isNewView && toolbar}
              <div className="fp-plan-scroll">
                <table className="fp-plan-table">
                  <thead>
                    <tr>
                      <th
                        className="fp-plan-header-cell fp-plan-header-cell--process"
                        scope="col"
                        aria-label="Process column">
                        <span className="fp-plan-row-label-inner">
                          <ToggleButton
                            open={allExpanded}
                            onClick={allExpanded ? collapseAll : expandAll}
                            label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                          />
                          <span>Process</span>
                        </span>
                      </th>
                      <th className="fp-plan-header-cell fp-plan-header-cell--pvt" scope="col">
                        PVT
                      </th>
                      {intervals.map((iv, idx) => (
                        <th
                          key={idx}
                          className={`fp-plan-header-cell fp-plan-header-cell--slot${
                            idx === slotCount - 1 ? ' is-last' : ''
                          }`}
                          scope="col">
                          <div className="fp-summary-slot-line">
                            <span className="fp-summary-slot-primary">{iv.name || `P${idx + 1}`}</span>
                            {iv.startLabel && iv.endLabel && (
                              <span className="fp-summary-slot-secondary">{`${iv.startLabel}–${iv.endLabel}`}</span>
                            )}
                            {iv.endTime && iv.startTime && (
                              <span className="fp-summary-slot-tertiary">
                                {formatDuration(iv.endTime - iv.startTime)}
                              </span>
                            )}
                          </div>
                          {!isNewView && <div className="fp-summary-slot-indicator" data-status="info" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.flatMap((row) => {
                      if (row.hidden) return [];
                      const rendered = renderNode(
                        row,
                        0,
                        expanded,
                        toggle,
                        selectedMetric,
                        actualsCtx,
                        endedByInterval,
                        false,
                        isNewView,
                        distByKey,
                        blankVolumeKeys
                      );
                      if (row.key === wipBeforeKey && stowWIP.length > 0) {
                        return [<WipRow key="__wip__" stowWIP={stowWIP} columnCount={slotCount} />, ...rendered];
                      }
                      return rendered;
                    })}
                  </tbody>
                </table>
              </div>
            </Column>
          </Expander>
        </div>
      </Column>
    </div>
  );
};

export default LaborPlanGrid;
